/**
 * Analytics Service (Web)
 *
 * Centralized Mixpanel tracking for the web app.
 * Mirrors the mobile analytics.service.ts API surface,
 * adapted for mixpanel-browser.
 */

import mixpanel from 'mixpanel-browser';
import type { AnalyticsEvent } from './analytics.types';

const FIRST_VISIT_KEY = 'mp_first_visit';

class AnalyticsService {
  private initialized = false;
  private isEnabled = false;
  private identified = false;
  private pendingEvents: Array<{ name: string; properties?: Record<string, any> }> = [];

  private static readonly STATIC_SUPER_PROPERTIES = {
    'App name': 'ShareCost',
    'Platform': 'Web',
  };

  /**
   * Initialize Mixpanel with EU API endpoint.
   * Call once at app startup (module-level in App.tsx).
   */
  initialize(): void {
    const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
    if (!token) {
      return;
    }

    try {
      const apiHost = import.meta.env.VITE_MIXPANEL_API_HOST as string | undefined;
      mixpanel.init(token, {
        ...(apiHost && { api_host: apiHost }),
        persistence: 'localStorage',
        track_pageview: false, // we track manually for SPA
      });

      mixpanel.register(AnalyticsService.STATIC_SUPER_PROPERTIES);

      this.initialized = true;
      this.isEnabled = true;
    } catch (error) {
      console.error('[Analytics] Initialization failed:', error);
      this.initialized = false;
      this.isEnabled = false;
    }
  }

  /**
   * Identify a user and set profile properties.
   * Flushes any events that were buffered before identity was known.
   *
   * The profile on buh_id may be shared with the Wallet app — sharedProps
   * (Mixpanel reserved $email/$name) are therefore written via set_once so
   * the Wallet-set values are not overwritten. ShareCost-specific values go
   * via set() under an sc_* namespace where overwrite is intended.
   */
  identify(
    userId: string,
    sharedProps?: Record<string, any>,
    scProps?: Record<string, any>,
  ): void {
    if (!this.isReady()) return;

    try {
      mixpanel.identify(userId);

      if (sharedProps) {
        mixpanel.people.set_once(sharedProps);
      }
      if (scProps) {
        mixpanel.people.set(scProps);
      }

      this.identified = true;
      this.flushPendingEvents();
    } catch (error) {
      console.error('[Analytics] Identify failed:', error);
    }
  }

  /**
   * Reset user identity (call on logout).
   * Clears user ID and creates new anonymous ID.
   * Discards any buffered events from an incomplete identify flow.
   */
  reset(): void {
    if (!this.isReady()) return;

    try {
      this.identified = false;
      this.pendingEvents = [];
      mixpanel.reset();
      // reset() clears all super properties — re-register the static ones
      mixpanel.register(AnalyticsService.STATIC_SUPER_PROPERTIES);
    } catch (error) {
      console.error('[Analytics] Reset failed:', error);
    }
  }

  /**
   * Register super properties sent with every event.
   */
  setSuperProperties(properties: Record<string, any>): void {
    if (!this.isReady()) return;
    mixpanel.register(properties);
  }

  /**
   * Track a custom event with properties.
   */
  track(event: AnalyticsEvent): void;
  track(eventName: string, properties?: Record<string, any>): void;
  track(
    eventOrName: AnalyticsEvent | string,
    properties?: Record<string, any>,
  ): void {
    if (!this.isReady()) return;

    try {
      let eventName: string;
      let eventProps: Record<string, any> | undefined;

      if (typeof eventOrName === 'string') {
        eventName = eventOrName;
        eventProps = properties;
      } else {
        eventName = eventOrName.name;
        eventProps = eventOrName.properties;
      }

      if (!this.identified) {
        this.pendingEvents.push({ name: eventName, properties: eventProps });
        return;
      }

      mixpanel.track(eventName, eventProps);
    } catch (error) {
      console.error('[Analytics] Track failed:', error);
    }
  }

  /**
   * Emit 'First Visit' on the user's first load of the web app, detected via
   * a localStorage flag. Analogous to the mobile 'App Installed' event.
   * The event flows through the pendingEvents buffer so it lands on the
   * correct distinct_id (buh_id or sharecost_id) once identify() completes.
   */
  trackFirstVisit(): void {
    if (!this.isReady()) return;
    try {
      if (!localStorage.getItem(FIRST_VISIT_KEY)) {
        this.track('First Visit');
        localStorage.setItem(FIRST_VISIT_KEY, Date.now().toString());
      }
    } catch (error) {
      console.error('[Analytics] trackFirstVisit failed:', error);
    }
  }

  /**
   * Track a page view (web-specific, replaces mobile's trackScreen).
   */
  trackPageView(path: string): void {
    if (!this.isReady()) return;

    const eventName = 'Page Viewed';
    const eventProps = { path };

    if (!this.identified) {
      this.pendingEvents.push({ name: eventName, properties: eventProps });
      return;
    }

    try {
      mixpanel.track(eventName, eventProps);
    } catch (error) {
      console.error('[Analytics] Page view tracking failed:', error);
    }
  }

  /**
   * Set user profile properties (incremental update).
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady()) return;

    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.error('[Analytics] Set user properties failed:', error);
    }
  }

  /**
   * Send all events that were buffered before identify() was called.
   */
  private flushPendingEvents(): void {
    const events = this.pendingEvents;
    this.pendingEvents = [];
    for (const event of events) {
      try {
        mixpanel.track(event.name, event.properties);
      } catch (error) {
        console.error('[Analytics] Flush event failed:', error);
      }
    }
  }

  private isReady(): boolean {
    return this.initialized && this.isEnabled;
  }

  get isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const Analytics = new AnalyticsService();
